// MaturaArithmeticDifferenceClosedTask.js
// Zadanie maturalne – zamknięte (ciąg arytmetyczny):
// dane są dwa wyrazy a_{n1}, a_{n2}; szukamy różnicy r (ABCD),
// w połowie przypadków jednym z wyrazów jest a_1.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function shuffle(array) {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ----------------- ułamki (do zapisu różnicy r) -----------------

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a || 1;
}

function makeFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function fracEqual(a, b) {
  return a.num === b.num && a.den === b.den;
}

function fracToLatex(frac) {
  const { num, den } = frac;
  if (den === 1) return String(num);
  const sign = num < 0 ? "-" : "";
  const absNum = Math.abs(num);
  return `${sign}\\dfrac{${absNum}}{${den}}`;
}

// pomocniczo – ładny zapis liczby całkowitej w danych (z nawiasem dla ujemnych)
function intToLatex(n) {
  if (n < 0) {
    return `(${n})`;
  }
  return String(n);
}

// ======================= GENERATOR =======================

/**
 * Wariant „oryginał” – matura podstawowa, czerwiec 2025, zadanie 15.1:
 *   ciąg arytmetyczny, a_1 = 52, a_25 = 2;  r = (2-52)/24 = -25/12  (A).
 */
function createJune2025OriginalTask() {
  const correctFrac = { num: -25, den: 12 };
  const options = [
    { value: { num: -25, den: 12 }, latex: "-\\dfrac{25}{12}", label: "A" },
    { value: { num: -2, den: 1 }, latex: "-2", label: "B" },
    { value: { num: 2, den: 1 }, latex: "2", label: "C" },
    { value: { num: 25, den: 12 }, latex: "\\dfrac{25}{12}", label: "D" },
  ];

  return {
    type: "MaturaArithmeticDifferenceClosedTask",
    data: {
      withA1: true,
      n1: 1,
      n2: 25,
      a_n1: 52,
      a_n2: 2,
      swapOrder: false,
      correctFrac,
      options,
      correctIndex: 0,
      maxPoints: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: "15.1",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  // wariant: true => podane a_1 i a_k, false => dwa dowolne wyrazy
  const withA1 = Math.random() < 0.5;

  let n1;
  let n2;

  if (withA1) {
    n1 = 1;
    // drugi indeks w okolicach 10–30, żeby różnica była sensowna jak w arkuszu
    n2 = randInt(10, 30);
  } else {
    n1 = randInt(1, 25);
    n2 = randInt(n1 + 1, Math.min(35, n1 + 25)); // różnica indeksów ≤ 25
  }

  const k = n2 - n1; // odległość między indeksami (mianownik przy liczeniu r)

  // losujemy różnicę r tak, by |r| nie było zbyt duże (≤ 25)
  let D; // różnica wartości: a_{n2} - a_{n1} = k * r
  let rFrac;

  do {
    // różnica wartości między wyrazami – coś w okolicach -80..80
    D = randInt(-80, 80);
    if (D === 0) continue;
  } while (D === 0 || Math.abs(D / k) > 25); // niezerowa różnica i ograniczenie na |r|

  rFrac = makeFrac(D, k); // poprawna różnica r = D / k

  // wybieramy wartość pierwszego z dwóch podanych wyrazów
  const aFirst = withA1 ? randInt(15, 35) : randInt(15, 35);
  const aSecond = aFirst + D;

  const a_n1 = aFirst;
  const a_n2 = aSecond;

  // czy w treści chcemy wypisać a_{n1} najpierw, czy a_{n2} – losowo (żeby czasem były "wymieszane")
  const swapOrder = !withA1 && Math.random() < 0.5;

  // --------- odpowiedzi (ABCD) ---------
  // POPRAWKA: zawsze dwie pary ± (jak na maturze), żeby nie dało się „po znaku” odgadnąć.
  const correctFrac = rFrac;
  const correctNeg = makeFrac(-correctFrac.num, correctFrac.den);

  // kandydaci na „złą” wartość w (potem tworzymy ±w)
  const candidates = [];

  // (a) brak dzielenia przez różnicę indeksów – tylko różnica wartości
  const noGap = makeFrac(D, 1);
  candidates.push(noGap);

  // (b) dzielenie przez "zły" mianownik: przez n2 zamiast (n2 - n1)
  const wrongDenN2 = makeFrac(D, n2);
  candidates.push(wrongDenN2);

  // (c) typowy błąd: przez (n2 - n1 + 1) zamiast (n2 - n1)
  const wrongDenOffBy1 = makeFrac(D, k + 1);
  candidates.push(wrongDenOffBy1);

  // wybieramy taki w, żeby nie był równy ±r
  let wrongFrac =
    shuffle(candidates).find(
      (f) =>
        !fracEqual(f, correctFrac) &&
        !fracEqual(f, correctNeg) &&
        !(f.num === 0)
    ) || noGap;

  // jeśli przez przypadek wypadło ±r (mało prawdopodobne), „dokręcamy” w o 1 w liczniku
  if (fracEqual(wrongFrac, correctFrac) || fracEqual(wrongFrac, correctNeg)) {
    wrongFrac = makeFrac(wrongFrac.num + (wrongFrac.num >= 0 ? 1 : -1), wrongFrac.den);
  }

  const wrongNeg = makeFrac(-wrongFrac.num, wrongFrac.den);

  // zestaw odpowiedzi: ±r oraz ±w
  const allFracs = shuffle([correctFrac, correctNeg, wrongFrac, wrongNeg]);

  const options = allFracs.map((frac, idx) => ({
    value: frac,
    latex: fracToLatex(frac),
    label: LETTERS[idx],
  }));

  const correctIndex = allFracs.findIndex((f) => fracEqual(f, correctFrac));

  return {
    type: "MaturaArithmeticDifferenceClosedTask",
    data: {
      withA1,
      n1,
      n2,
      a_n1,
      a_n2,
      swapOrder,
      correctFrac,
      options,
      correctIndex,
      maxPoints: 1,
    },
  };
}

// ======================= TREŚĆ =======================

export function renderTask({ data }) {
  const { withA1, n1, n2, a_n1, a_n2, swapOrder } = data;

  const idx1 = n1;
  const idx2 = n2;

  const val1 = intToLatex(a_n1);
  const val2 = intToLatex(a_n2);

  let firstIndex, firstVal, secondIndex, secondVal;

  if (swapOrder) {
    firstIndex = idx2;
    firstVal = val2;
    secondIndex = idx1;
    secondVal = val1;
  } else {
    firstIndex = idx1;
    firstVal = val1;
    secondIndex = idx2;
    secondVal = val2;
  }

  return (
    <div style={{ textAlign: "left" }} className="space-y-2">
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        W ciągu arytmetycznym <InlineMath math={'(a_n)'} />, określonym dla
        każdej liczby naturalnej <InlineMath math={'n \\ge 1'} />, dane są{" "}
        wyrazy:
      </p>
      <p>
        <InlineMath
          math={`a_{${firstIndex}} = ${firstVal} \\quad \\text{oraz} \\quad a_{${secondIndex}} = ${secondVal}.`}
        />
      </p>
      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <p>
        Różnica ciągu <InlineMath math={'(a_n)'} /> jest równa
      </p>

      <div style={{ marginTop: 8 }}>
        {data.options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ======================= INPUT =======================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ======================= PODGLĄD ODP. =======================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    userAnswer.label == null ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ======================= SPRAWDZANIE =======================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ======================= POPRAWNA ODP. =======================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

// ======================= WYJAŚNIENIE =======================

export function renderExplanation({ data }) {
  const { n1, n2, a_n1, a_n2, correctFrac } = data;

  const d = n2 - n1;
  const diffValues = a_n2 - a_n1;

  return (
    <div style={{ textAlign: "left" }} className="space-y-3">
      <p>
        <strong>Krok 1.</strong> Korzystamy ze wzoru łączącego dwa wyrazy ciągu arytmetycznego:
      </p>
      <BlockMath math={`a_{${n2}} = a_{${n1}} + (${n2}-${n1})\\,r`} />

      <p>
        <strong>Krok 2.</strong> Rozwiązujemy to równanie względem różnicy <InlineMath math={'r'} />:
      </p>
      <BlockMath math={`r = \\dfrac{a_{${n2}}-a_{${n1}}}{${n2}-${n1}}`} />

      <p>
        <strong>Krok 3.</strong> Podstawiamy dane z treści zadania i obliczamy:
      </p>
      <BlockMath
        math={`r = \\dfrac{${a_n2}-${a_n1}}{${n2}-${n1}} = \\dfrac{${diffValues}}{${d}} = ${fracToLatex(correctFrac)}`}
      />

      <p>
        Zatem różnica ciągu <InlineMath math={'(a_n)'} /> jest równa{" "}
        <InlineMath math={fracToLatex(correctFrac)} />
        <InlineMath math={'\\text{.}'} />
      </p>
    </div>
  );
}

// ======================= EXPORT DOMYŚLNY =======================

export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation,
};
